/**
 * The problem report control.
 *
 * `ReportProblem` sits in the application shell. A person describes the
 * problem in a sentence; the report carries the page and the recent request
 * trail. The same component sends a signal on its own when the trail shows a
 * repeated failure or a repeated click, and it then invites the person to
 * add words.
 */

import { useMutation } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { api } from "../lib/api.ts";
import { useProblem } from "../lib/problem.ts";
import { onSignal, recentTrail, watchClicks, type Signal } from "../lib/trail.ts";
import { Button, Problem, classes, inputClass } from "./primitives.tsx";
import { useDismiss } from "./use-dismiss.ts";

const INVITE: Record<Signal, string> = {
  repeated_click: "That control did not seem to respond. Tell us what you expected.",
  repeated_failure: "That action failed several times. Tell us what you were doing.",
};

export function ReportProblem() {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [invite, setInvite] = useState<string | null>(null);
  const { problem, setProblem, onError } = useProblem();
  const [sent, setSent] = useState(false);
  const panel = useRef<HTMLDivElement | null>(null);
  const field = useRef<HTMLTextAreaElement | null>(null);

  useDismiss(panel, open, () => setOpen(false));

  useEffect(() => {
    if (open) field.current?.focus();
  }, [open]);

  // The signals: write a record, then invite the person to add words.
  useEffect(() => {
    const stopClicks = watchClicks();
    const stopSignals = onSignal((signal) => {
      void api
        .post("/api/problem-reports", {
          kind: "signal",
          signal,
          page: window.location.pathname,
          trail: recentTrail(),
        })
        .catch(() => undefined);
      setInvite(INVITE[signal]);
      setSent(false);
    });
    return () => {
      stopClicks();
      stopSignals();
    };
  }, []);

  const send = useMutation({
    mutationFn: () =>
      api.post("/api/problem-reports", {
        kind: "report",
        page: window.location.pathname,
        message,
        trail: recentTrail(),
      }),
    onSuccess: () => {
      setMessage("");
      setProblem(null);
      setInvite(null);
      setSent(true);
    },
    onError,
  });

  // The header wraps on a narrow window, and the button can then sit at the
  // left edge. The panel opens toward the side with room, so it stays on screen.
  const anchor = useRef<HTMLDivElement>(null);
  const [side, setSide] = useState<"left" | "right">("right");
  const placeBy = () => {
    const box = anchor.current?.getBoundingClientRect();
    setSide(box !== undefined && box.right < 400 ? "left" : "right");
  };

  const toggle = () => {
    placeBy();
    setOpen(!open);
    setSent(false);
  };

  return (
    <div className="relative" ref={anchor}>
      <Button size="small" ariaExpanded={open} onClick={toggle}>
        Report a problem
      </Button>
      {invite !== null && !open && (
        <button
          type="button"
          className={`absolute ${side === "left" ? "left-0" : "right-0"} top-full z-30 mt-1 w-72 rounded-arc border border-highlight bg-paper p-3 text-left text-sm shadow-lg`}
          onClick={() => {
            placeBy();
            setOpen(true);
          }}
        >
          {invite}
        </button>
      )}
      {open && (
        <div
          ref={panel}
          role="dialog"
          aria-label="Report a problem"
          className={`absolute ${side === "left" ? "left-0" : "right-0"} top-full z-40 mt-1 flex w-96 max-w-[calc(100vw-2rem)] flex-col gap-3 rounded-arc border border-divider bg-paper p-3 shadow-xl`}
        >
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              send.mutate();
            }}
          >
            <label htmlFor="problem-report-message" className="font-medium">
              What went wrong?
            </label>
            {invite !== null && <p className="text-sm text-muted">{invite}</p>}
            <textarea
              id="problem-report-message"
              ref={field}
              className={classes(inputClass, "min-h-24")}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder="What you did, and what you expected."
              maxLength={4000}
            />
            <p className="text-sm text-muted">
              The report includes this page and your recent requests. It never includes what you typed elsewhere.
            </p>
            {problem !== null && <Problem message={problem} />}
            {sent && (
              <p role="status" className="text-sm text-success">
                The report was sent.
              </p>
            )}
            <div className="flex gap-2">
              <Button type="submit" disabled={send.isPending}>
                Send report
              </Button>
              <Button variant="secondary" type="button" onClick={() => setOpen(false)}>
                Close
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
